import { CSSResultGroup, html } from 'lit';
import { customElement, query, state, property } from 'lit/decorators.js';
import componentStyles from './tufts-quote-slider.css.lit';
import Splide from '@splidejs/splide';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { ResizeController } from '../../controllers/resize-controller';
import { classMap } from 'lit/directives/class-map.js';

/**
 * Tufts Quote Slider
 * @element tufts-quote-slider
 *
 * @slot slider
 */

@customElement('tufts-quote-slider')
export class TuftsQuoteSlider extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  _splide: Splide;
  resizeController: ResizeController = new ResizeController(this);

  @property({ type: Boolean, reflect: true })
  limited = false;

  @query('#splide2')
  _splideRef: HTMLElement;
  @query('.splide__list')
  _splideList: HTMLElement;
  @query('slot')
  _slotRef: HTMLSlotElement;
  @state() numberSlide: number;

  firstUpdated() {
    this.numberSlide = this._slotRef.assignedNodes().length;
    if (this.numberSlide !== 1) {
      this.initialSlide();
    }
  }

  // This method updates the slides in the carousel.
  // It gets the nodes assigned to the slot, filters out any that are not HTMLElements,
  // converts the remaining nodes to HTML strings, and inserts them into the carousel.
  initialSlide() {
    // Filter nodes to exclude those without images when limited=true
    const slides = this._slotRef
      .assignedNodes()
      .filter(node => node instanceof HTMLElement)
      .filter(node => {
        // If limited=true, verify that the quote-block has an image
        if (this.limited) {
          // We know node is HTMLElement from the previous filter
          const htmlNode = node as HTMLElement;
          const quoteBlock = htmlNode.querySelector('tufts-quote-block');
          if (!quoteBlock) return false;

          // Check if the quote-block has an image slot
          const hasImage = quoteBlock.querySelector('[slot="image"]') !== null;
          return hasImage;
        }
        return true;
      })
      .map(node => {
        (node as HTMLElement).classList.add('splide__slide');
        if (this.limited && node instanceof HTMLElement) {
          // Find the tufts-quote-block element and set the limited attribute on it
          const quoteBlock = (node as HTMLElement).querySelector(
            'tufts-quote-block'
          );
          if (quoteBlock) {
            quoteBlock.setAttribute('limited', '');
          }
        }
        return (node as HTMLElement).outerHTML;
      });

    // Ensure that the number of slides to select is not greater than the number of available slides
    const slidesToSelect = Math.min(3, slides.length);

    // Generate a set of unique random indexes
    const selectedIndexes = new Set<number>();
    while (selectedIndexes.size < slidesToSelect) {
      const randomIndex = Math.floor(Math.random() * slides.length);
      selectedIndexes.add(randomIndex);
    }

    // Convert the selected slides to HTML strings and insert them into the carousel
    const selectedSlides = Array.from(selectedIndexes)
      .map(index => slides[index])
      .join('');

    this._splideList.innerHTML = selectedSlides;

    this.createReferenceSplide();
  }

  createReferenceSplide() {
    if (this._splideRef) {
      this._splide = new Splide(this._splideRef, {
        perPage: 1,
        type: 'slide',
        focus: 'center',
      }).mount();
    }
  }

  render() {
    const classes = {
      splide: true,
      mobile: this.resizeController.currentBreakpointRange === 0,
      desktop: this.resizeController.currentBreakpointRange === 1,
    };

    return this.numberSlide !== 1
      ? html`
          <div id="splide2" class="${classMap(classes)}">
            <div class="splide__track">
              <ul class="splide__list">
                <slot name="slides"></slot>
              </ul>
            </div>
          </div>
        `
      : html` <slot name="slides"></slot> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-quote-slider': TuftsQuoteSlider;
  }
}
